import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "@/lib/toast";
import { AdminFocusTarget } from "@/lib/admin-focus";
import { SiteShell } from "@/components/SiteShell";
import { AdminNav } from "@/components/admin/AdminNav";
import { ImageUploadField } from "@/components/admin/ImageUploadField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { TEXT_POSITIONS, TEXT_POSITION_LABELS_AR } from "@/lib/text-position";
import { cleanBlockText } from "@/lib/block-text";

import {
  getHomeAboutBlock,
  setHomeAboutBlock,
  type HomeAboutBlock,
} from "@/utils/homeAboutBlock.functions";
import {
  getHomeBlockOpacity,
  setHomeBlockOpacity,
} from "@/utils/homeBlockOpacity.functions";
import { BLOCK_OPACITY_PREVIEW_MSG } from "@/components/HomeBlockOpacityWrap";

/** slot → مفتاح البلوك في نظام الشفافيّة */
const OPACITY_KEY_BY_SLOT = {
  primary: "AboutIntro",
  secondary: "AboutIntroTwo",
  tertiary: "AboutIntroThree",
} as const;

import { AdminLinkPicker } from "@/components/admin/AdminLinkPicker";
export const Route = createFileRoute("/admin/home-about")({
  validateSearch: (search: Record<string, unknown>) => ({
    slot:
      search.slot === "secondary"
        ? ("secondary" as const)
        : search.slot === "tertiary"
          ? ("tertiary" as const)
          : ("primary" as const),
  }),
  head: () => ({
    meta: [
      { title: "بلوك ٢–٤ — إدارة" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminHomeAboutPage,
});

function AdminHomeAboutPage() {
  const { slot } = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const fetchFn = useServerFn(getHomeAboutBlock);
  const saveFn = useServerFn(setHomeAboutBlock);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-home-about-block", slot],
    queryFn: () => fetchFn({ data: { slot } }),
  });

  const fetchOpacity = useServerFn(getHomeBlockOpacity);
  const saveOpacityFn = useServerFn(setHomeBlockOpacity);
  const { data: opacityData } = useQuery({
    queryKey: ["admin-home-block-opacity"],
    queryFn: () => fetchOpacity({ data: {} }),
  });
  const opacityKey = OPACITY_KEY_BY_SLOT[slot];
  const storedOpacity = (opacityData?.opacity as
    | Record<string, { mobile: number; desktop: number }>
    | undefined)?.[opacityKey] ?? { mobile: 100, desktop: 100 };
  const [opacityDraft, setOpacityDraft] = useState(storedOpacity);
  useEffect(() => {
    setOpacityDraft(storedOpacity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(storedOpacity), slot]);
  const opacityDirty = JSON.stringify(opacityDraft) !== JSON.stringify(storedOpacity);

  const [draft, setDraft] = useState<HomeAboutBlock | null>(null);
  useEffect(() => {
    setDraft(null);
  }, [slot]);
  useEffect(() => {
    if (data?.block && !draft) setDraft(data.block);
  }, [data, draft]);

  const save = useMutation({
    mutationFn: async (v: HomeAboutBlock) => {
      const cleaned: HomeAboutBlock = {
        ...v,
        eyebrowAr: cleanBlockText(v.eyebrowAr),
        eyebrowEn: cleanBlockText(v.eyebrowEn),
        titleAr: cleanBlockText(v.titleAr),
        titleEn: cleanBlockText(v.titleEn),
        bodyAr: cleanBlockText(v.bodyAr),
        bodyEn: cleanBlockText(v.bodyEn),
      };
      setDraft(cleaned);
      await saveFn({ data: { ...cleaned, slot } });

      if (opacityDirty && opacityData?.opacity) {
        await saveOpacityFn({
          data: { opacity: { ...opacityData.opacity, [opacityKey]: opacityDraft } },
        });
      }
    },
    onSuccess: () => {
      toast.success("تمّ الحفظ");
      qc.invalidateQueries({ queryKey: ["admin-home-about-block", slot] });
      qc.invalidateQueries({ queryKey: ["home-about-block", slot] });
      qc.invalidateQueries({ queryKey: ["admin-home-block-opacity"] });
      qc.invalidateQueries({ queryKey: ["home-block-opacity"] });
    },
    onError: (e: Error) => toast.error(e.message || "فشل الحفظ"),
  });

  const set = <K extends keyof HomeAboutBlock>(k: K, v: HomeAboutBlock[K]) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d));

  const isDirty =
    (!!draft && !!data?.block && JSON.stringify(draft) !== JSON.stringify(data.block)) ||
    opacityDirty;


  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-[1600px] px-4 py-8">
        <AdminNav />
        <div className="mb-6 mt-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl">بلوك ٢–٤</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              كلّ بلوك: صورة/فيديو والنصّ فوقها، أو نصّ فقط بلا وسائط. أيّ حقل تتركه فارغاً يبقى مخفيّاً — لا نصوص افتراضيّة.
            </p>

            <div className="mt-3 flex gap-2">
              {(["primary", "secondary", "tertiary"] as const).map((s2) => (
                <Button
                  key={s2}
                  size="sm"
                  variant={slot === s2 ? "default" : "outline"}
                  onClick={() => navigate({ search: { slot: s2 } })}
                >
                  {s2 === "primary" ? "بلوك ٢" : s2 === "secondary" ? "بلوك ٣" : "بلوك ٤"}
                </Button>
              ))}
            </div>
          </div>
          <Button
            onClick={() => draft && save.mutate(draft)}
            disabled={!isDirty || save.isPending}
          >
            {save.isPending ? "جارٍ الحفظ…" : "حفظ"}
          </Button>
        </div>

        {isDirty ? (
          <div className="fixed inset-x-0 bottom-4 z-40 flex justify-center px-4">
            <div className="flex items-center gap-3 rounded-full border border-border bg-background/95 px-4 py-2 shadow-lg backdrop-blur">
              <span className="text-xs text-muted-foreground">تعديلات غير محفوظة</span>
              <Button
                size="sm"
                onClick={() => draft && save.mutate(draft)}
                disabled={save.isPending}
              >
                {save.isPending ? "جارٍ الحفظ…" : "حفظ"}
              </Button>
            </div>
          </div>
        ) : null}


        {isLoading || !draft ? (
          <div className="text-sm text-muted-foreground">جارٍ التحميل…</div>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">التفعيل</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-4">
                  <Label htmlFor="hab-enabled">إظهار البلوك في الهوم</Label>
                  <Switch
                    id="hab-enabled"
                    checked={draft.enabled}
                    onCheckedChange={(v) => set("enabled", v)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">سطر فوق العنوان (eyebrow)</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label>عربي</Label>
                  <Input
                    value={draft.eyebrowAr}
                    onChange={(e) => set("eyebrowAr", e.target.value)}
                  />
                </div>
                <div>
                  <Label>English</Label>
                  <Input
                    dir="ltr"
                    value={draft.eyebrowEn}
                    onChange={(e) => set("eyebrowEn", e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">العنوان الكبير</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label>عربي</Label>
                  <Textarea
                    rows={3}
                    value={draft.titleAr}
                    onChange={(e) => set("titleAr", e.target.value)}
                  />
                </div>
                <div>
                  <Label>English</Label>
                  <Textarea
                    dir="ltr"
                    rows={3}
                    value={draft.titleEn}
                    onChange={(e) => set("titleEn", e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            <AdminFocusTarget id="text-position">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">ترتيب النصّ داخل البلوك</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    اختر موضع النصّ (٩ مواقع) — نفس نظام الهيرو. المواضع فيزيائيّة (يسار يبقى
                    يسار في العربيّة أيضاً).
                  </p>
                  <div className="grid max-w-[360px] grid-cols-3 gap-2" dir="ltr">
                    {TEXT_POSITIONS.map((pos) => {
                      const active = (draft.textPosition ?? "center") === pos;
                      return (
                        <Button
                          key={pos}
                          type="button"
                          variant={active ? "default" : "outline"}
                          className="h-14 text-[11px] leading-tight"
                          onClick={() => set("textPosition", pos)}
                        >
                          {TEXT_POSITION_LABELS_AR[pos]}
                        </Button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </AdminFocusTarget>

            <AdminFocusTarget id="media">
<Card>
              <CardHeader>
                <CardTitle className="text-base">صورة البلوك</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ImageUploadField
                  label="صورة/فيديو البلوك"
                  value={draft.imageUrl}
                  allowVideo
                  onChange={(url) => set("imageUrl", url)}
                  folder="home-about"
                  previewHeight={140}
                  hint="الوسائط تملأ البلوك كخلفيّة والنصّ يظهر فوقها. النسبة المناسبة 16:9 أو 3:2. اتركها فارغة ليصبح البلوك نصّاً فقط."
                />


                <div className="space-y-2 rounded-md border border-border p-3">
                  <Label>لون الخلفيّة (بدلاً من الصورة)</Label>
                  <p className="text-xs text-muted-foreground">
                    اترك حقل الصورة فارغاً واختر لوناً — سيظهر البلوك بخلفيّة لونيّة صافية.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      aria-label="لون الخلفيّة"
                      value={draft.bgColor || "#111111"}
                      onChange={(e) => set("bgColor", e.target.value)}
                      className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent p-1"
                    />
                    <Input
                      dir="ltr"
                      lang="en"
                      placeholder="#111111"
                      value={draft.bgColor}
                      onChange={(e) => set("bgColor", e.target.value)}
                      className="max-w-[140px]"
                    />
                    {draft.bgColor ? (
                      <Button variant="outline" size="sm" onClick={() => set("bgColor", "")}>
                        مسح اللون
                      </Button>
                    ) : null}
                  </div>
                </div>
              </CardContent>
            </Card>
            </AdminFocusTarget>


            <Card>
              <CardHeader>
                <CardTitle className="text-base">شفافيّة خلفيّة البلوك</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  ١٠٠٪ = معتم تماماً · ٠٪ = شفّاف كلّيّاً فتظهر أرضيّة الصفحة.
                </p>
                {([
                  ["mobile", "الموبايل"],
                  ["desktop", "الديسكتوب"],
                ] as const).map(([dev, label]) => (
                  <div key={dev} className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span>{label}</span>
                      <span className="tabular-nums text-muted-foreground" dir="ltr">
                        {opacityDraft[dev]}%
                      </span>
                    </div>
                    <Slider
                      value={[opacityDraft[dev]]}
                      min={0}
                      max={100}
                      step={5}
                      onValueChange={([v]) => {
                        const next = { ...opacityDraft, [dev]: v ?? 100 };
                        setOpacityDraft(next);
                        const payload = {
                          type: BLOCK_OPACITY_PREVIEW_MSG,
                          source: "quick-edit",
                          opacity: { [opacityKey]: next },
                        };
                        window.postMessage(payload, "*");
                        if (window.parent && window.parent !== window) {
                          window.parent.postMessage(payload, "*");
                        }
                      }}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>



            <Card>
              <CardHeader>
                <CardTitle className="text-base">النصّ التعريفيّ</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label>عربي</Label>
                  <Textarea
                    rows={7}
                    value={draft.bodyAr}
                    onChange={(e) => set("bodyAr", e.target.value)}
                  />
                </div>
                <div>
                  <Label>English</Label>
                  <Textarea
                    dir="ltr"
                    rows={7}
                    value={draft.bodyEn}
                    onChange={(e) => set("bodyEn", e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">الزرّ (CTA)</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label>نصّ الزرّ — عربي</Label>
                  <Input
                    value={draft.ctaLabelAr}
                    onChange={(e) => set("ctaLabelAr", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Button label — English</Label>
                  <Input
                    dir="ltr"
                    value={draft.ctaLabelEn}
                    onChange={(e) => set("ctaLabelEn", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <AdminLinkPicker
                    label="رابط الزرّ"
                    placeholder="/about"
                    value={draft.ctaHref}
                    onChange={(v) => set("ctaHref", v)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </SiteShell>
  );
}
